import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import { 
  Truck, 
  MapPin, 
  CheckCircle, 
  Clock, 
  Navigation, 
  Car,
  Calendar,
  AlertCircle
} from "lucide-react";
import L from "leaflet";
import Header2 from "@/components/Header2";
import Footer from "@/components/Footer";

// Custom vehicle icon for map
const vehicleIcon = new L.Icon({
  iconUrl: 'data:image/svg+xml;base64,' + btoa(`
    <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="18" fill="#3B82F6" stroke="#1E40AF" stroke-width="2"/>
      <path d="M12 16h16v8H12v-8z" fill="white"/>
      <circle cx="16" cy="26" r="2" fill="white"/>
      <circle cx="24" cy="26" r="2" fill="white"/>
      <rect x="14" y="12" width="12" height="4" fill="white"/>
    </svg>
  `),
  iconSize: [40, 40],
  iconAnchor: [20, 40],
  popupAnchor: [0, -40]
});

const API_BASE_URL = `${import.meta.env?.VITE_BACKEND_URL || 'http://localhost:3001'}`;

export default function TrackPage() {
  const { token } = useParams();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [mapType, setMapType] = useState<'street' | 'satellite' | 'terrain'>('street');

  useEffect(() => {
    console.log("🔍 TrackPage mounted. Token din URL:", token);
    console.log("🌐 API_BASE_URL este:", API_BASE_URL);
    
    fetch(`${API_BASE_URL}/track/${token}`)
      .then(res => {
        console.log("📡 Răspuns brut de la backend:", res);
        return res.json();
      })
      .then(json => {
        console.log("✅ Răspuns JSON de la backend:", json);
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error("❌ Eroare la fetch:", err);
        setData({ error: true, message: "Eroare de conectare la server" });
        setLoading(false);
      });
  }, [token]);

  const getTileLayerConfig = () => {
    switch (mapType) {
      case 'satellite':
        return {
          url: "https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}",
          attribution: "Esri, Maxar, Earthstar Geographics"
        };
      case 'terrain':
        return {
          url: "https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png",
          attribution: "© OpenTopoMap"
        };
      default:
        return {
          url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
          attribution: "© OpenStreetMap contributors"
        };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
        <Header2 />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-lg font-medium text-gray-700">Se încarcă informațiile...</p>
              <p className="text-sm text-gray-500 mt-2">Vă rugăm să așteptați</p>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (data?.error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex flex-col">
        <Header2 />
        <div className="flex-1 flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full mx-4">
            <div className="text-center">
              <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h1 className="text-2xl font-bold text-gray-800 mb-2">Ups! Ceva nu este în regulă</h1>
              <p className="text-gray-600 mb-4">{data.message || "Link invalid sau expirat"}</p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <p className="text-sm text-red-700">
                  Verificați din nou linkul de tracking sau contactați expeditorul pentru un link actualizat.
                </p>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  const vehicle = data?.vehicle;
  const location = vehicle?.location;
  const hasValidCoords = location?.latitude && location?.longitude && 
                        !isNaN(location.latitude) && !isNaN(location.longitude);

  const tileConfig = getTileLayerConfig();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex flex-col">
      <Header2 />
      
      <div className="flex-1 container mx-auto px-4 py-6 max-w-6xl">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl mb-6 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-6">
            <div className="flex items-center space-x-4">
              <div className="bg-white/20 p-3 rounded-xl">
                <Truck className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white mb-1">Status Transport</h1>
                <p className="text-blue-100">Urmăriți în timp real transportul dvs.</p>
              </div>
            </div>
          </div>

          {/* Status Section */}
          <div className="p-6">
            <div className="flex items-center justify-center mb-6">
              <div className={`flex items-center space-x-3 px-6 py-4 rounded-full text-lg font-semibold ${
                data.status === "pe_drum" 
                  ? "bg-blue-100 text-blue-800" 
                  : data.status === "in_asteptare"
                  ? "bg-yellow-100 text-yellow-800"
                  : data.status === "livrat"
                  ? "bg-green-100 text-green-800"
                  : data.status === "in_incarcare"
                  ? "bg-orange-100 text-orange-800"
                  : "bg-gray-100 text-gray-800"
              }`}>
                {data.status === "pe_drum" ? (
                  <>
                    <Clock className="w-6 h-6" />
                    <span>Transportul este pe drum</span>
                  </>
                ) : data.status === "in_asteptare" ? (
                  <>
                    <Clock className="w-6 h-6" />
                    <span>In asteptare</span>
                  </>
                ) : data.status === "livrat" ? (
                  <>
                    <CheckCircle className="w-6 h-6" />
                    <span>Livrat</span>
                  </>
                ) : data.status === "in_incarcare" ? (
                  <>
                    <Truck className="w-6 h-6" />
                    <span>In incarcare</span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-6 h-6" />
                    <span>Status necunoscut</span>
                  </>
                )}
              </div>
            </div>

            {/* Vehicle Details */}
            {vehicle && (
              <div className={data.status === "pe_drum" ? "grid md:grid-cols-2 gap-6" : "flex justify-center"}>
                <div className="bg-gray-50 rounded-xl p-6">
                  <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                    <Car className="w-6 h-6 mr-2 text-blue-600" />
                    Detalii Vehicul
                  </h2>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center py-2 border-b border-gray-200">
                      <span className="text-gray-600">Număr înmatriculare:</span>
                      <span className="font-bold text-lg text-gray-800">{vehicle.plate_number}</span>
                    </div>
                    {data.status === "pe_drum" && location && location.timestamp && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 flex items-center">
                          <Calendar className="w-4 h-4 mr-1" />
                          Ultima actualizare:
                        </span>
                        <span className="font-medium text-gray-800">
                          {new Date(location.timestamp).toLocaleString('ro-RO')}
                        </span>
                      </div>
                    )}
                    {data.status === "livrat" && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 flex items-center">
                          <CheckCircle className="w-4 h-4 mr-1" />
                          Status:
                        </span>
                        <span className="font-medium text-green-600">Comanda a fost livrată cu succes!</span>
                      </div>
                    )}
                    {data.status === "in_asteptare" && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 flex items-center">
                          <Clock className="w-4 h-4 mr-1" />
                          Status:
                        </span>
                        <span className="font-medium text-yellow-600">Comanda este în așteptare pentru preluare.</span>
                      </div>
                    )}
                    {data.status === "in_incarcare" && (
                      <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600 flex items-center">
                          <Truck className="w-4 h-4 mr-1" />
                          Status:
                        </span>
                        <span className="font-medium text-orange-600">Comanda este în proces de încărcare în vehicul.</span>
                      </div>
                    )}
                  </div>
                </div>

                {data.status === "pe_drum" && (
                  <div className="bg-gray-50 rounded-xl p-6">
                    <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center">
                      <MapPin className="w-6 h-6 mr-2 text-red-600" />
                      Locație Actuală
                    </h2>
                    {location ? (
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4 border border-gray-200">
                          <div className="flex items-start space-x-2">
                            <Navigation className="w-5 h-5 text-blue-500 mt-0.5 flex-shrink-0" />
                            <div>
                              <p className="font-medium text-gray-800 break-words">
                                {location.address || 'Adresa nu este disponibilă'}
                              </p>
                              <p className="text-sm text-gray-600 mt-1">
                                Coordonate: {location.latitude}, {location.longitude}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                        <p className="text-yellow-800">Informațiile despre locație nu sunt disponibile momentan.</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Map Section */}
        {location && hasValidCoords && data.status === "pe_drum" && (
          <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
            <div className="p-4 border-b border-gray-200 bg-gradient-to-r from-green-50 to-blue-50">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-800 flex items-center">
                  <MapPin className="w-6 h-6 mr-2 text-green-600" />
                  Localizare pe Hartă
                </h2>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setMapType('street')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      mapType === 'street' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Străzi
                  </button>
                  <button
                    onClick={() => setMapType('satellite')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      mapType === 'satellite' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Satelit
                  </button>
                  <button
                    onClick={() => setMapType('terrain')}
                    className={`px-3 py-1 rounded-lg text-sm font-medium transition-colors ${
                      mapType === 'terrain' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Teren
                  </button>
                </div>
              </div>
            </div>

            <div className="h-96 relative">
              <MapContainer
                center={[location.latitude, location.longitude]}
                zoom={15}
                style={{ height: '100%', width: '100%' }}
                zoomControl={true}
                scrollWheelZoom={true}
              >
                <TileLayer
                  url={tileConfig.url}
                  attribution={tileConfig.attribution}
                  maxZoom={18}
                />
                
                {mapType === 'satellite' && (
                  <TileLayer
                    url="https://{s}.basemaps.cartocdn.com/rastertiles/voyager_only_labels/{z}/{x}/{y}{r}.png"
                    attribution="&copy; CARTO"
                    opacity={0.7}
                  />
                )}

                <Marker position={[location.latitude, location.longitude]} icon={vehicleIcon}>
                  <Popup className="custom-popup">
                    <div className="p-3 min-w-[200px]">
                      <div className="font-bold text-lg mb-2 text-gray-800">
                        Vehicul: {vehicle.plate_number}
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center">
                          <MapPin className="w-4 h-4 mr-2 text-red-500" />
                          <span className="break-words">
                            {location.address || 'Adresa indisponibilă'}
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          Ultima actualizare: {new Date(location.timestamp).toLocaleString('ro-RO')}
                        </div>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              </MapContainer>
            </div>
          </div>
        )}

        {/* Info Footer */}
        <div className="text-center mt-8">
          <p className="text-gray-600 text-sm">
            Informațiile sunt actualizate în timp real. Pentru întrebări contactați expeditorul.
          </p>
        </div>
      </div>

      <Footer />

      {/* Custom CSS for popup styling */}
      <style>{`
        .custom-popup .leaflet-popup-content-wrapper {
          border-radius: 12px;
          box-shadow: 0 10px 25px rgba(0,0,0,0.15);
        }
        .custom-popup .leaflet-popup-tip {
          background: white;
        }
        .leaflet-popup-content {
          margin: 0;
        }
        .leaflet-popup-close-button {
          padding: 8px !important;
          font-size: 18px !important;
        }
      `}</style>
    </div>
  );
}